'use client';
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import toast from 'react-hot-toast';
import { Truck, ArrowLeft, Building2, Mail, Globe, User, Send, MapPin } from 'lucide-react';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import PageAtmosphere from '@/components/PageAtmosphere';
import { useT } from '@/lib/useT';
import { LANG_STORAGE_KEY } from '@/lib/useT';
import { api } from '@/lib/api';

/* ── Lightweight SVG road animation ─────────────────────────────── */
function RoadAnimation() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf: number;
    let t = 0;

    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    canvas.width = W;
    canvas.height = H;

    /* Truck entity */
    const trucks = [
      { x: -80, y: H * 0.55, speed: 0.55, scale: 1 },
      { x: W * 0.4, y: H * 0.72, speed: 0.38, scale: 0.75 },
    ];
    /* Package entity */
    const boxes = [
      { x: W * 0.15, y: H * 0.3, vx: 0.4, vy: -0.12 },
      { x: W * 0.65, y: H * 0.2, vx: 0.55, vy: 0.08 },
    ];

    function drawTruck(cx: number, cy: number, s: number) {
      ctx!.save();
      ctx!.translate(cx, cy);
      ctx!.scale(s, s);
      ctx!.strokeStyle = 'color-mix(in srgb, var(--accent) 18%, transparent)';
      ctx!.lineWidth = 1.5;
      ctx!.lineJoin = 'round';
      // Cab
      ctx!.beginPath();
      ctx!.rect(0, -14, 18, 20);
      ctx!.stroke();
      // Trailer
      ctx!.beginPath();
      ctx!.rect(-46, -12, 48, 18);
      ctx!.stroke();
      // Wheels
      [{ x: -36, y: 6 }, { x: -16, y: 6 }, { x: 10, y: 6 }].forEach(w => {
        ctx!.beginPath();
        ctx!.arc(w.x, w.y, 4, 0, Math.PI * 2);
        ctx!.stroke();
      });
      // Exhaust puffs
      ctx!.fillStyle = 'color-mix(in srgb, var(--accent) 7%, transparent)';
      ctx!.beginPath(); ctx!.arc(22, -18, 4 + Math.sin(t * 3) * 2, 0, Math.PI * 2); ctx!.fill();
      ctx!.beginPath(); ctx!.arc(26, -24, 2.5 + Math.sin(t * 3 + 1) * 1.5, 0, Math.PI * 2); ctx!.fill();
      ctx!.restore();
    }

    function drawBox(cx: number, cy: number) {
      ctx!.save();
      ctx!.translate(cx, cy);
      ctx!.strokeStyle = 'color-mix(in srgb, var(--live) 15%, transparent)';
      ctx!.lineWidth = 1.2;
      // Box outline
      ctx!.beginPath(); ctx!.rect(-8, -8, 16, 16); ctx!.stroke();
      // Cross on top
      ctx!.beginPath(); ctx!.moveTo(-8, -8); ctx!.lineTo(0, -14); ctx!.lineTo(8, -8); ctx!.stroke();
      ctx!.beginPath(); ctx!.moveTo(0, -14); ctx!.lineTo(0, -8); ctx!.stroke();
      ctx!.restore();
    }

    function drawRoad(y: number, alpha: number) {
      ctx!.strokeStyle = `rgba(255,255,255,${alpha})`;
      ctx!.lineWidth = 1;
      ctx!.setLineDash([20, 30]);
      ctx!.lineDashOffset = -(t * 60) % 50;
      ctx!.beginPath(); ctx!.moveTo(0, y); ctx!.lineTo(W, y); ctx!.stroke();
      ctx!.setLineDash([]);
    }

    function frame() {
      ctx!.clearRect(0, 0, W, H);
      t += 0.008;

      // Roads
      drawRoad(H * 0.6, 0.06);
      drawRoad(H * 0.77, 0.04);

      // Trucks
      trucks.forEach(tr => {
        tr.x += tr.speed;
        if (tr.x > W + 100) tr.x = -100;
        drawTruck(tr.x, tr.y, tr.scale);
      });

      // Floating boxes
      boxes.forEach(b => {
        b.x += b.vx; b.y += b.vy;
        if (b.x > W + 20) b.x = -20;
        if (b.y < -20) b.y = H + 20;
        drawBox(b.x, b.y);
      });

      raf = requestAnimationFrame(frame);
    }

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.9 }}
    />
  );
}

// Defaults are kept inline for fast first paint while the API call resolves —
// they match the most-recent legal entity on file. Anything saved by
// super-admin via /super-admin/contact-info overrides these on render.
const DEFAULT_INFO: Record<string, string> = {
  contact_company_name: 'MATTALNET GRUPP',
  contact_company_legal: 'OÜ',
  contact_company_street: 'Tuukri tn 19-315',
  contact_company_city: '10120 Tallinn',
  contact_company_country: 'Estonia',
  contact_reg_code: '14744075',
  contact_vat: 'EE102519059',
  contact_eori: '14744075',
  contact_duns: '536415812',
  contact_person_name: 'Andreas A. Gleim',
  contact_email: '',
  contact_phone: '',
  contact_website: 'logixa.one',
};

export default function KontaktPage() {
  const t = useT();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [info, setInfo] = useState<Record<string, string>>(DEFAULT_INFO);

  useEffect(() => {
    api.get<Record<string, string>>('/public/contact-info')
      .then(r => {
        // Merge: API value wins when present + non-empty, else fall back to default
        const merged: Record<string, string> = { ...DEFAULT_INFO };
        for (const [k, v] of Object.entries(r || {})) {
          if (typeof v === 'string' && v.trim().length > 0) merged[k] = v;
        }
        setInfo(merged);
      })
      .catch(() => {/* keep defaults */});
  }, []);
  const fullCompany = `${info.contact_company_name} ${info.contact_company_legal}`.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.subject.trim().length < 2 || form.message.trim().length < 10) {
      toast.error(t('contact_validation_failed'));
      return;
    }
    setSending(true);
    let lang: string | null = null;
    try { lang = typeof window !== 'undefined' ? localStorage.getItem(LANG_STORAGE_KEY) : null; } catch {}
    try {
      await api.post('/public/contact', { ...form, lang });
      setSent(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err: any) {
      toast.error(err?.message || t('contact_send_failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--c-bg)' }}>
      {/* Background road animation */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <RoadAnimation />
      </div>

      <div className="relative" style={{ zIndex: 1 }}>
      <PublicHeader />
      <PageAtmosphere />

      {/* ════════════ CONTENT ════════════ */}
      <main className="px-6 md:px-8 py-16 max-w-4xl mx-auto">

        <div className="animate-fade-up mb-12">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-ink mb-4">{t('contact_title')}</h1>
          <p style={{ color: 'var(--c-muted)' }}>{t('contact_subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Company Info Card */}
          <div className="space-y-6">
            <div className="logixa-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--c-accent)' }}>
                  <Building2 size={20} />
                </div>
                <h2 className="text-lg font-display font-bold text-ink">{t('contact_company')}</h2>
              </div>
              <div className="space-y-3 text-sm" style={{ color: 'var(--c-text)' }}>
                <p className="text-ink font-semibold">{fullCompany}</p>
                <p>{info.contact_company_street}</p>
                <p>{info.contact_company_city}{info.contact_company_country ? `, ${info.contact_company_country}` : ''}</p>
                <div className="pt-3 space-y-2" style={{ borderTop: '1px solid var(--c-border)' }}>
                  {info.contact_reg_code && <p><span style={{ color: 'var(--c-muted)' }}>Registrikood:</span> {info.contact_reg_code}</p>}
                  {info.contact_vat && <p><span style={{ color: 'var(--c-muted)' }}>VAT:</span> {info.contact_vat}</p>}
                  {info.contact_eori && <p><span style={{ color: 'var(--c-muted)' }}>EORI:</span> {info.contact_eori}</p>}
                  {info.contact_duns && <p><span style={{ color: 'var(--c-muted)' }}>D-U-N-S:</span> {info.contact_duns}</p>}
                </div>
              </div>
            </div>

            {/* Contact Person */}
            <div className="logixa-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'color-mix(in srgb, var(--live) 10%, transparent)', color: 'var(--c-green)' }}>
                  <User size={20} />
                </div>
                <h2 className="text-lg font-display font-bold text-ink">{t('contact_person')}</h2>
              </div>
              <div className="space-y-3 text-sm" style={{ color: 'var(--c-text)' }}>
                <p className="text-ink font-semibold">{info.contact_person_name}</p>
                {info.contact_phone && (
                  <div className="flex items-center gap-2">
                    <Mail size={14} style={{ color: 'var(--c-muted)' }} />
                    <a href={`tel:${info.contact_phone}`} className="transition-colors hover:text-ink" style={{ color: 'var(--c-accent)' }}>
                      {info.contact_phone}
                    </a>
                  </div>
                )}
                {info.contact_website && (
                  <div className="flex items-center gap-2">
                    <Globe size={14} style={{ color: 'var(--c-muted)' }} />
                    <a href={`https://${info.contact_website.replace(/^https?:\/\//, '')}`} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-ink" style={{ color: 'var(--c-accent)' }}>
                      {info.contact_website}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Address / Map */}
            <div className="logixa-card p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--accent)' }}>
                  <MapPin size={20} />
                </div>
                <h2 className="text-lg font-display font-bold text-ink">{t('contact_location')}</h2>
              </div>
              <div className="rounded-xl p-4 space-y-1.5 text-sm" style={{ background: 'var(--c-hover)', border: '1px solid var(--c-border)' }}>
                <p className="font-semibold text-ink">{fullCompany}</p>
                <p style={{ color: 'var(--c-text)' }}>{info.contact_company_street}</p>
                <p style={{ color: 'var(--c-text)' }}>{info.contact_company_city}{info.contact_company_country ? `, ${info.contact_company_country}` : ''}</p>
                <div className="pt-2 mt-2 space-y-1 text-xs" style={{ borderTop: '1px solid var(--c-border)', color: 'var(--c-muted)' }}>
                  {info.contact_reg_code && <p>Reg. {info.contact_reg_code}</p>}
                  {info.contact_vat && <p>VAT {info.contact_vat}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="logixa-card p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.1)', color: 'var(--c-amber)' }}>
                <Send size={20} />
              </div>
              <h2 className="text-lg font-display font-bold text-ink">{t('contact_send')}</h2>
            </div>

            {sent ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'color-mix(in srgb, var(--live) 10%, transparent)', color: 'var(--c-green)' }}>
                  <Send size={24} />
                </div>
                <h3 className="text-lg font-display font-bold text-ink mb-2">{t('contact_sent_title')}</h3>
                <p className="text-sm" style={{ color: 'var(--c-text)' }}>
                  {t('contact_sent_body')}
                </p>
                <button onClick={() => setSent(false)} className="btn-ghost mt-6 text-sm">
                  {t('contact_send_another')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label block mb-2">{t('contact_name')} *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="logixa-input"
                    placeholder={t('contact_name_ph')}
                  />
                </div>
                <div>
                  <label className="label block mb-2">{t('login_email')} *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="logixa-input"
                    placeholder={t('contact_email_ph')}
                  />
                </div>
                <div>
                  <label className="label block mb-2">{t('contact_subject')} *</label>
                  <input
                    type="text"
                    required
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="logixa-input"
                    placeholder={t('contact_subject_ph')}
                  />
                </div>
                <div>
                  <label className="label block mb-2">{t('contact_message')} *</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="logixa-input resize-none"
                    placeholder={t('contact_message_ph')}
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                >
                  {sending ? t('contact_sending') : (
                    <>{t('contact_send')} <Send size={16} /></>
                  )}
                </button>
                <p className="text-xs" style={{ color: 'var(--c-muted)' }}>
                  {t('contact_consent')}{' '}
                  <Link href="https://logixa.one/datenschutz" className="underline transition-colors hover:text-ink">{t('contact_privacy_link')}</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </main>

      <PublicFooter />
      </div>{/* /relative z-1 wrapper */}
    </div>
  );
}
